import {Component, OnInit} from "@angular/core";
import {NotificationService} from "../../../core/services/notification.service";
import {InvoicesControllerService} from "../../../core/api/controllers/invoices-controller.service";
import {IInvoice} from "../../../shared/interfaces/IInvoice";

interface IInvoiceYearGroup {
  year: number;
  invoices: IInvoice[];
}

@Component({
  selector: "app-invoices",
  templateUrl: "./invoices.component.html",
  styleUrls: ["./invoices.component.scss"],
})
export class InvoicesComponent implements OnInit {
  invoices: IInvoice[] = [];
  isLoading = false;
  searchTerm = "";
  readonly skeletonRows = [0, 1, 2, 3];

  constructor(
    private invoicesController: InvoicesControllerService,
    private notificationService: NotificationService,
  ) {}

  ngOnInit(): void {
    this.getInvoices();
  }

  get filteredInvoices(): IInvoice[] {
    const term = this.searchTerm.trim().toLowerCase();
    if (!term) {
      return this.invoices;
    }
    return this.invoices.filter((invoice) =>
      this.searchableText(invoice).includes(term),
    );
  }

  /** Newest first, split into year sections. */
  get invoiceGroups(): IInvoiceYearGroup[] {
    const groups: IInvoiceYearGroup[] = [];
    [...this.filteredInvoices]
      .sort(
        (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
      )
      .forEach((invoice) => {
        const year = new Date(invoice.date).getFullYear();
        const group = groups.find((candidate) => candidate.year === year);
        if (group) {
          group.invoices.push(invoice);
        } else {
          groups.push({ year, invoices: [invoice] });
        }
      });
    return groups;
  }

  get latestInvoiceDate(): Date {
    if (!this.invoices.length) {
      return null;
    }
    return this.invoices
      .map((invoice) => new Date(invoice.date))
      .reduce((latest, date) => (date > latest ? date : latest));
  }

  clearSearch(): void {
    this.searchTerm = "";
  }

  trackByInvoiceId(index: number, invoice: IInvoice): string {
    return invoice.id;
  }

  /**
   * Invoice files are named `MM_DD_YYYY_<name>.pdf`; the date already has its
   * own line, so the prefix and the extension are dropped from the title.
   */
  displayName(invoice: IInvoice): string {
    return (
      invoice.name
        ?.replace(/^\d{2}_\d{2}_\d{4}_/, "")
        .replace(/\.pdf$/i, "")
        .trim() || invoice.name
    );
  }

  private searchableText(invoice: IInvoice): string {
    const date = new Date(invoice.date);
    const formatted = isNaN(date.getTime())
      ? ""
      : date.toLocaleDateString(undefined, {
          day: "numeric",
          month: "long",
          year: "numeric",
        });
    return `${invoice.name ?? ""} ${formatted}`.toLowerCase();
  }

  getInvoices() {
    this.isLoading = true;
    this.invoicesController.getInvoices().subscribe(
      (response) => {
        this.invoices = response;
        this.isLoading = false;
      },
      (error) => {
        this.notificationService.error(error);
        this.isLoading = false;
      },
    );
  }

  openInvoice(invoiceUrl: string) {
    window.open(invoiceUrl, "_blank");
  }
}
